/**
 * Deutsche Bezeichnungen der Verkehrsmittel fuer die Stauaufschlag-Oberflaeche.
 *
 * Deckt genau die 17 Verkehrsmittel ab, fuer die ein Aufschlag konfigurierbar
 * ist (siehe `table_controlls/plz_surcharge.py` im Backend). Die Schluessel
 * sind die CleverRoute-Transport-IDs.
 *
 * Bewusst eine eigene Liste und keine Wiederverwendung von `transportTypeMap`
 * in `TheParameters.vue`: dort sind mehrere Eintraege auskommentiert, und die
 * Map ist an das dortige Formular gebunden. Welche Verkehrsmittel wirklich
 * zulaessig sind, meldet ohnehin das Backend ueber
 * `/plz-surcharges/transports` - diese Liste liefert nur die Beschriftung.
 */
export const TRANSPORT_LABELS: Record<string, string> = {
  car: "Pkw",
  car_electric: "E-Auto",
  train: "ÖV",
  park_and_ride: "Park & Ride",
  bike_and_ride: "Bike & Ride",
  eBike: "Pedelec",
  bike: "Fahrrad",
  walk: "Zu Fuß",
  bikescooter: "BikeScooter",
  taxi: "Taxi",
  ride_and_taxi: "Taxi & Ride",
  eScooter: "E-Scooter",
  escooter_and_ride_first_last_escooter: "E-Scooter & Ride",
  werksbus: "Werksbus",
  bike_and_werksbus: "Bike & Werksbus",
  bahnreaktivierung_and_ride: "Bahnreaktivierung & Ride",
  park_ride_and_bike: "Park & Ride & Bike",
};

/**
 * Beschriftungen der Karte: Spaltenpraefix der Datenbank -> Name in der
 * Legende.
 *
 * Die Ergebnisspalten (`Zeit_effektivstes_VM` und Geschwister) liefern
 * Praefixe wie `Bike_and_Ride`, nicht die CleverRoute-Transport-IDs von
 * `TRANSPORT_LABELS`. Deshalb eine zweite Liste: dieselben Verkehrsmittel,
 * anderer Schluesselraum.
 *
 * Massgeblich sind die Namen aus der Kartenlegende (`transportTypes` in
 * `TheMapEditor.vue`) - Popup und Legende stehen nebeneinander auf demselben
 * Bildschirm, und derselbe Marker darf dort nicht zweimal anders heissen. Die
 * Ergebnistabelle schreibt "Bike & Ride" statt "Bike+Ride"; das ist eine
 * eigene Ansicht und hier bewusst nicht das Vorbild.
 *
 * Enthalten sind auch die Schreibweisen, die die Views daneben verwenden
 * (`OeV_Fahrrad`, `Auto_OeV`, `Fahrrad_Werksbus`, `Taxi_OeV`, `eScooter_OeV`)
 * - welche davon ankommt, haengt an der abgefragten Spalte, und ein
 * unbekannter Schluessel fiele sonst wieder auf den Rohwert zurueck.
 */
export const TRANSPORT_COLUMN_LABELS: Record<string, string> = {
  PKW: "Pkw",
  E_Auto: "E-Pkw",
  OeV: "ÖV",
  Fahrrad: "Fahrrad",
  Pedelec: "Pedelec",
  Fuss_Weg: "Fußweg",
  Park_and_Ride: "Park+Ride",
  Auto_OeV: "Park+Ride",
  Bike_and_Ride: "Bike+Ride",
  OeV_Fahrrad: "Bike+Ride",
  Werksbus: "Werksbus",
  Bike_and_Werksbus: "Bike+Werksbus",
  Fahrrad_Werksbus: "Bike+Werksbus",
  Bahnreakt_Ride: "Bahnreaktivierung",
  Park_Ride_and_Bike: "Park+Ride+Bike",
  // Ohne Eintrag in der Legende - die Karten zeichnen sie nicht, die
  // Ergebnisspalten koennen sie aber liefern.
  Taxi: "Taxi",
  Taxi_and_Ride: "Taxi+Ride",
  Taxi_OeV: "Taxi+Ride",
  eScooter: "E-Scooter",
  eScooter_and_Ride: "E-Scooter+Ride",
  eScooter_OeV: "E-Scooter+Ride",
  BikeScooter: "BikeScooter",
};

/**
 * Beschriftungen der Karte: interner Name -> Name in der Legende.
 *
 * Die Karten "Beste Alternative" und "Anzahl Alternativen" rechnen nicht mit
 * Spaltenpraefixen, sondern mit eigenen Bezeichnern ("Auto", "Zu Fuß",
 * "Bike & Ride"). Die sahen im Popup zwar lesbar aus, stimmten aber nicht mit
 * der Legende daneben ueberein.
 *
 * Nicht gelistete Bezeichner ("Taxi", "Carsharing") stehen schon so da, wie
 * sie angezeigt werden sollen, und fallen auf sich selbst zurueck.
 */
export const TRANSPORT_INTERNAL_LABELS: Record<string, string> = {
  Auto: "Pkw",
  "E-Auto": "E-Pkw",
  "Zu Fuß": "Fußweg",
  Fußweg: "Fußweg",
  "Park & Ride": "Park+Ride",
  "Bike & Ride": "Bike+Ride",
  "Bike & Werksbus": "Bike+Werksbus",
  "Bahnreaktivierung & Ride": "Bahnreaktivierung",
  "Park & Ride & Bike": "Park+Ride+Bike",
  "e-Scooter": "E-Scooter",
};

/** Beschriftung eines Verkehrsmittels, mit der ID als Rueckfallebene. */
export function transportLabel(transportType: string): string {
  return TRANSPORT_LABELS[transportType] ?? transportType;
}
